import { useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
// types
import { paths } from 'src/routes/paths';
// components
import { stringAvatar } from 'src/utils/avatar';
import { fDateTime } from 'src/utils/format-time';
import { enqueueSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import { ClassRegistrationService, ClazzDto, ParticipationStatus, SignUpDto } from '../../api';
import { useLocales } from '../../locales';
import CustomPopover, { usePopover } from '../../components/custom-popover';
import { getClass } from '../../redux/slices/classes';
import { useDispatch } from '../../redux/store';
import AddParticipantForm from './add-participant-form';
import { useBoolean } from '../../hooks/use-boolean';
import { useRouter } from '../../routes/hook';
import CheckInMap from './check-in-map';

// ----------------------------------------------------------------------

type Props = {
  participants: SignUpDto[];
  clazz: ClazzDto;
};

export default function ClassParticipants({ participants, clazz }: Props) {
  const { t } = useLocales();

  const dispatch = useDispatch();

  const router = useRouter();

  const form = useBoolean();

  const theme = useTheme();

  const handleRemove = async (p: SignUpDto) => {
    await ClassRegistrationService.edit({
      id: p.id!,
      body: {
        id: p.id!,
        status: ParticipationStatus.OptedOut,
      },
    });

    enqueueSnackbar(t('Sign up removed!'));

    dispatch(getClass(p.classId!));
    popover.onClose();
  };

  const handlePresent = async (p: SignUpDto) => {
    await ClassRegistrationService.edit({
      id: p.id!,
      body: {
        id: p.id!,
        status: ParticipationStatus.OnTime,
      },
    });

    enqueueSnackbar(t('Member is checked in!'));

    dispatch(getClass(p.classId!));
    popover.onClose();
  };

  const handleCreate = async () => {
    dispatch(getClass(clazz.id!));
  };

  const popover = usePopover();

  const [selectedSignUp, setSelectedSignUp] = useState<SignUpDto | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, row: SignUpDto) => {
    setSelectedSignUp(row);
    popover.onOpen(event);
  };

  const rows = [...participants];

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'member',
      headerName: t('Member'),
      sortable: false,
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Stack
          direction="row"
          sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          onClick={() => router.push(paths.members.view(params.row.accountId!))}
        >
          {params.row?.profileImageUrl === undefined && (
            <Avatar
              {...stringAvatar(`${params.row?.name}`, {
                mr: 2,
                width: 48,
                height: 48,
              })}
            />
          )}
          {params.row?.profileImageUrl !== undefined && (
            <Avatar alt={`${params.row?.name}`} src={params.row?.profileImageUrl} sx={{ mr: 2 }} />
          )}

          <ListItemText
            primary={`${params.row?.name}`}
            secondary={params.row?.accountEmailAddress}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
          />
        </Stack>
      ),
    },
    {
      field: 'signedUpAt',
      headerName: t('Signed up'),
      flex: 1,
      minWidth: 100,
      valueFormatter: (value) => fDateTime(value),
    },
    {
      field: 'checkedInAt',
      headerName: t('Checked in'),
      flex: 1,
      minWidth: 100,
      valueFormatter: (value) => (value ? fDateTime(value) : ''),
    },
    {
      field: 'optedOutAt',
      headerName: t('Opted out'),
      flex: 1,
      minWidth: 100,
      valueFormatter: (value) => (value ? fDateTime(value) : ''),
    },
    {
      field: 'status',
      headerName: t('Status'),
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Chip
          color={
            (params.row.status === ParticipationStatus.OnTime && 'success') ||
            ((params.row.status === ParticipationStatus.NeverMet ||
              params.row.status === ParticipationStatus.TooLate) &&
              'error') ||
            (params.row.status === ParticipationStatus.OnWaitingList && 'warning') ||
            'default'
          }
          label={t(`${params.value}`)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t(''),
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <IconButton
          color={popover.open ? 'inherit' : 'default'}
          onClick={(e) => handleOpenPopover(e, params.row)}
        >
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {t('Participants')}
                </Typography>
              </Box>

              <Box sx={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={form.onTrue}
                  startIcon={<Iconify icon="eva:plus-fill" />}
                >
                  {t('Add')}
                </Button>
              </Box>
            </Stack>

            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'status', sort: 'asc' }],
                },
                pagination: {
                  paginationModel: {
                    pageSize: 25,
                  },
                },
              }}
              rowHeight={60}
              pageSizeOptions={[5, 10, 25]}
              checkboxSelection={false}
              disableRowSelectionOnClick
            />
          </Stack>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {clazz.gym && (
            <Stack spacing={3}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {t('Check in map')}
                </Typography>
              </Box>

              <CheckInMap participants={clazz.participants ?? []} gym={clazz.gym} />
            </Stack>
          )}
        </Grid>
      </Grid>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={form.value}
        onClose={form.onFalse}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: theme.transitions.duration.shortest - 80,
        }}
      >
        <DialogTitle sx={{ minHeight: 76 }}>{t('Add participant')}</DialogTitle>

        <AddParticipantForm
          currentClazz={clazz}
          onClose={() => {
            handleCreate();
            form.onFalse();
          }}
        />
      </Dialog>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="top-right">
        {selectedSignUp?.status !== ParticipationStatus.OnTime && (
          <MenuItem
            onClick={() => {
              handlePresent(selectedSignUp!);
            }}
          >
            <Iconify icon="material-symbols:order-approve-sharp" />
            {t('Present')}
          </MenuItem>
        )}

        {selectedSignUp?.status !== ParticipationStatus.OptedOut && (
          <MenuItem
            onClick={() => {
              handleRemove(selectedSignUp!);
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            {t('Remove')}
          </MenuItem>
        )}
      </CustomPopover>
    </>
  );
}
